import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { Empty, PageHeading, Spinner } from '../components/ui';

export function Classes() {
  const classes = useQuery({ queryKey: ['classes'], queryFn: api.classes.list });
  const today = new Date().toISOString().slice(0, 10);

  if (classes.isLoading) return <Spinner label="Loading classes" />;

  // A platform administrator outside a tenant is refused here by design.
  if (classes.error instanceof ApiError && classes.error.status === 403) {
    return (
      <div>
        <PageHeading eyebrow="Take attendance" title="Classes" />
        <Empty
          title="No organization open"
          hint="You are signed in as a platform administrator. Choose an organization to work inside it."
        />
        <Link to="/enter" className="btn mt-4">
          Choose an organization
        </Link>
      </div>
    );
  }

  const all = classes.data?.classes ?? [];

  /*
   * A retired class is not offered for marking. It stays in the database and
   * keeps every mark ever taken against it, which is the whole reason classes
   * retire rather than being deleted, but it is finished: there is no next
   * session to take attendance for.
   *
   * Filtered here rather than in the endpoint, because the same query feeds two
   * administrative pages that need the retired ones. The Classes tab lists them
   * behind its own filter, and the holiday page needs their names so an old
   * closure still says which class it applied to.
   */
  const rows = all.filter((klass) => klass.isActive);

  return (
    <div>
      <PageHeading eyebrow="Take attendance" title="Classes" />

      {rows.length === 0 ? (
        <Empty
          title={all.length > 0 ? 'Nothing to mark' : 'No classes yet'}
          hint={
            all.length > 0
              ? 'Every class you are assigned to has been retired. An organization administrator can bring one back.'
              : 'An organization administrator creates classes and assigns you to them. Once assigned, they appear here.'
          }
        />
      ) : (
        <div className="grid">
          {rows.map((klass) => (
            <Link
              key={klass.id}
              to="/classes/$classId"
              params={{ classId: klass.id }}
              search={{ date: today }}
              className="group flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-b border-rule py-4 transition-colors last:border-0 hover:bg-paper-sunk"
            >
              <p className="text-lg font-semibold tracking-[-0.01em] underline-offset-4 group-hover:underline">
                {klass.name}
              </p>
              <div className="flex items-center gap-5 font-mono text-xs text-ink-soft tabular">
                <span>
                  {klass.studentCount} student{klass.studentCount === 1 ? '' : 's'}
                </span>
                <span>
                  {klass.periodCount} period{klass.periodCount === 1 ? '' : 's'}
                </span>
                <GoChevron />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The mark that says a row goes somewhere.
 *
 * These rows had only a hover tint, which says nothing at all until the pointer
 * is already on them and nothing ever to a touch screen. A chevron at the end
 * of a ruled row is the ordinary way to say "this opens", and it is the one
 * affordance available here: the design language keeps nothing rounded,
 * bordered and filled in a page's main region, so these cannot become cards.
 *
 * Same path as the ledger's disclosure chevron, unrotated, and ink-soft rather
 * than ink-faint because ink-faint measures 2.54:1 against the sheet.
 */
function GoChevron() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 10 10"
      aria-hidden="true"
      focusable="false"
      className="text-ink-soft transition-transform group-hover:translate-x-0.5"
    >
      <path
        d="M3 1 L7 5 L3 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
